import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { formatTimestamp } from "../lib/format";
import { cn, formatPrice } from "../lib/utils";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/forecasting")({
  component: desktopOnlyRoute(ForecastingRoute),
});

type PriceCurrency = "EUR" | "RON";
type DayStatus = "complete" | "partial" | "empty" | "error" | "missing";
type SourceGranularity = "HOUR" | "QH" | "MIXED" | null;

interface OpcomDamDay {
  deliveryDate: string;
  status: DayStatus;
  expectedRows: number;
  rowCount: number;
  sourceGranularity: SourceGranularity;
  sourceRowCount: number;
  normalizedRowCount: number;
  minPriceEurMwh: number | null;
  maxPriceEurMwh: number | null;
  avgPriceEurMwh: number | null;
  ropexBaseEurMwh: number | null;
  ropexPeakEurMwh: number | null;
  ropexOffPeakEurMwh: number | null;
  sourceUrl: string | null;
  fetchedAt: string | null;
  latestFetch: {
    status: string;
    startedAt: string;
    finishedAt: string;
    error: string | null;
    warnings: string[];
  } | null;
  fx: {
    rate: number;
    source: string;
    asOfDate: string | null;
  };
}

interface OpcomDamInterval {
  id: string;
  deliveryDate: string;
  qhIndex: number;
  intervalStart: string;
  intervalEnd: string;
  priceEurMwh: number | null;
  priceRonMwh: number | null;
  sourceGranularity: "HOUR" | "QH";
  sourceIntervalIndex: number;
  sourceIntervalCount: number;
  sourceUrl: string;
  fetchedAt: string;
  fx: {
    rate: number;
    source: string;
    asOfDate: string | null;
  };
}

interface OpcomDamResponse {
  fromDate: string;
  toDate: string;
  days: OpcomDamDay[];
  intervals: OpcomDamInterval[];
}

type ChartHover = {
  rowIndex: number;
  left: number;
  top: number;
};

type SelectionDrag = {
  startIndex: number;
  endIndex: number;
  mode: "add" | "remove";
};

type PriceStats = {
  count: number;
  avg: number | null;
  min: number | null;
  max: number | null;
};

type SelectionStats = PriceStats & {
  rangeLabel: string;
};

const CHART_WIDTH = 1200;
const CHART_HEIGHT = 330;
const CHART_LEFT = 58;
const CHART_RIGHT = 1164;
const PLOT_TOP = 24;
const PLOT_HEIGHT = 246;
const PLOT_BOTTOM = PLOT_TOP + PLOT_HEIGHT;
const CHART_EPS = 1e-9;
const CET_TIME_ZONE = "Europe/Berlin";

function ForecastingRoute() {
  const location = useLocation();
  return location.pathname === "/forecasting" ? <ForecastingPage /> : <Outlet />;
}

function ForecastingPage() {
  const queryClient = useQueryClient();
  const [fromDate, setFromDate] = useState(() => todayCetIso());
  const [useRange, setUseRange] = useState(false);
  const [toDate, setToDate] = useState(() => todayCetIso());
  const [currency, setCurrency] = useState<PriceCurrency>("EUR");
  const effectiveToDate = useRange ? toDate : fromDate;
  const fromDateValid = isIsoDate(fromDate);
  const toDateValid = isIsoDate(effectiveToDate);
  const rangeInvalid = fromDateValid && toDateValid && effectiveToDate < fromDate;
  const dateInvalid = !fromDateValid || !toDateValid || rangeInvalid;

  useEffect(() => {
    if (!useRange) setToDate(fromDate);
  }, [fromDate, useRange]);

  const query = useQuery({
    queryKey: ["forecasting", "opcom-dam", fromDate, effectiveToDate],
    enabled: !dateInvalid,
    queryFn: () => {
      const params = new URLSearchParams({ fromDate });
      if (effectiveToDate !== fromDate) params.set("toDate", effectiveToDate);
      return api<OpcomDamResponse>(`/forecasting/opcom-dam?${params.toString()}`);
    },
  });

  const refresh = useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>("/forecasting/opcom-dam/refresh", {
        method: "POST",
        body: JSON.stringify({
          fromDate,
          ...(effectiveToDate !== fromDate ? { toDate: effectiveToDate } : {}),
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["forecasting", "opcom-dam"] });
    },
  });

  const shownStats = useMemo(
    () => priceStats(query.data?.intervals ?? [], currency),
    [query.data?.intervals, currency]
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From date">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="bg-transparent border border-border rounded px-2 py-1 text-sm"
          />
        </Field>
        <label className="mb-1.5 inline-flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={useRange}
            onChange={(e) => setUseRange(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          Range
        </label>
        {useRange && (
          <Field label="To date">
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
        )}
        <Field label="Price currency">
          <div className="inline-flex overflow-hidden rounded border border-border text-sm">
            {(["EUR", "RON"] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                className={cn(
                  "px-3 py-1",
                  currency === c
                    ? "bg-accent text-bg"
                    : "bg-transparent text-muted hover:text-fg hover:bg-border/20"
                )}
                aria-pressed={currency === c}
              >
                {c}
              </button>
            ))}
          </div>
        </Field>
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending || dateInvalid}
          className="inline-flex items-center gap-2 border border-border rounded px-3 py-1.5 text-sm hover:border-accent disabled:opacity-50"
          title="Fetch OPCOM DAM data"
        >
          <RefreshCw className={cn("h-4 w-4", refresh.isPending && "animate-spin")} />
          Refresh OPCOM
        </button>
        <div className="ml-auto text-xs text-muted">
          Time: <span className="text-fg">CET market time</span>
        </div>
      </div>

      {dateInvalid && (
        <div className="text-negRed text-sm">
          {rangeInvalid ? "To date must be on or after from date." : "Select valid from/to dates."}
        </div>
      )}
      {refresh.isError && <div className="text-negRed text-sm">{(refresh.error as Error).message}</div>}
      {query.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {query.isError && <div className="text-negRed text-sm">{(query.error as Error).message}</div>}

      {query.data && !dateInvalid && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <StatCard label="Shown intervals" value={String(shownStats.count)} detail={rangeLabel(fromDate, effectiveToDate)} />
            <StatCard label={`Average ${unitLabel(currency)}`} value={formatPrice(shownStats.avg)} detail="visible curve" />
            <StatCard
              label={`Min / Max ${unitLabel(currency)}`}
              value={`${formatPrice(shownStats.min)} / ${formatPrice(shownStats.max)}`}
              detail="visible data"
            />
            <StatCard
              label="Coverage"
              value={coverageLabel(query.data.days)}
              detail={granularityLabel(query.data.days)}
            />
          </div>

          <DamPriceChart
            rows={query.data.intervals}
            currency={currency}
            isRange={fromDate !== effectiveToDate}
          />

          <CoveragePanel days={query.data.days} currency={currency} />
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-xs text-muted flex flex-col gap-1">
      {label}
      {children}
    </label>
  );
}

function StatCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="border border-border/60 rounded-md p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 font-mono text-lg text-fg">{value}</div>
      <div className="mt-1 text-[11px] text-muted">{detail}</div>
    </div>
  );
}

function DamPriceChart({
  rows,
  currency,
  isRange,
}: {
  rows: OpcomDamInterval[];
  currency: PriceCurrency;
  isRange: boolean;
}) {
  const [hover, setHover] = useState<ChartHover | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [dragSelect, setDragSelect] = useState<SelectionDrag | null>(null);
  const chartBoxRef = useRef<HTMLDivElement | null>(null);
  const rowSignature = rows.map((row) => row.id).join("|");

  useEffect(() => {
    setSelectedIds(new Set());
    setDragSelect(null);
  }, [rowSignature]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelectedIds(new Set());
        setDragSelect(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!dragSelect) return;
    const stopDragging = () => setDragSelect(null);
    window.addEventListener("mouseup", stopDragging);
    return () => window.removeEventListener("mouseup", stopDragging);
  }, [dragSelect]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(row.id)),
    [rows, selectedIds]
  );
  const selectionStats = useMemo(
    () => buildSelectionStats(selectedRows, currency),
    [selectedRows, currency]
  );

  if (rows.length === 0) {
    return (
      <div className="border border-border/60 rounded-md p-4 text-sm text-muted">
        No OPCOM DAM prices found for this date selection. Use Refresh OPCOM to fetch this range.
      </div>
    );
  }

  const values = rows.map((row) => priceFor(row, currency)).filter(isFiniteNumber);
  const priceRange = paddedRange(values);
  const plotWidth = CHART_RIGHT - CHART_LEFT;
  const step = rows.length > 0 ? plotWidth / rows.length : plotWidth;
  const xForIndex = (idx: number) => CHART_LEFT + (idx + 0.5) * step;
  const yForPrice = (value: number) =>
    PLOT_TOP + ((priceRange.max - value) / (priceRange.max - priceRange.min)) * PLOT_HEIGHT;
  const yTicks = makeTicks(priceRange.min, priceRange.max, 5);
  const xTicks = makeXTicks(rows, isRange);
  const hoveredRow = hover ? rows[hover.rowIndex] : undefined;
  const showPoints = rows.length <= 220;

  const setIntervalHover = (
    rowIndex: number,
    e: React.MouseEvent<SVGRectElement>
  ) => {
    const rect = chartBoxRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 282;
    const height = 180;
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const left = clamp(localX + 14, 8, Math.max(8, rect.width - width - 8));
    const top = clamp(localY + 14, 8, Math.max(8, rect.height - height - 8));
    setHover({ rowIndex, left, top });
  };

  const applySelectionRange = (
    startIndex: number,
    endIndex: number,
    mode: SelectionDrag["mode"]
  ) => {
    const min = Math.min(startIndex, endIndex);
    const max = Math.max(startIndex, endIndex);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (let i = min; i <= max; i++) {
        const row = rows[i];
        if (!row) continue;
        if (mode === "add") next.add(row.id);
        else next.delete(row.id);
      }
      return next;
    });
  };

  const beginSelection = (rowIndex: number, e: React.MouseEvent<SVGRectElement>) => {
    e.preventDefault();
    const row = rows[rowIndex];
    if (!row) return;
    const mode: SelectionDrag["mode"] = selectedIds.has(row.id) ? "remove" : "add";
    setDragSelect({ startIndex: rowIndex, endIndex: rowIndex, mode });
    applySelectionRange(rowIndex, rowIndex, mode);
  };

  const extendSelection = (rowIndex: number, e: React.MouseEvent<SVGRectElement>) => {
    setIntervalHover(rowIndex, e);
    if (!dragSelect || dragSelect.endIndex === rowIndex) return;
    applySelectionRange(dragSelect.startIndex, rowIndex, dragSelect.mode);
    setDragSelect({ ...dragSelect, endIndex: rowIndex });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setDragSelect(null);
  };

  return (
    <div className="border border-border/60 rounded-md bg-bg">
      <div className="px-3 py-2 border-b border-border/50 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="text-xs font-medium">OPCOM DAM price curve</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-px w-5 bg-accent" />
              Price {unitLabel(currency)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-4 rounded-[1px] bg-accent opacity-20" />
              selected intervals
            </span>
          </div>
        </div>
        <div className="text-xs text-muted">
          Click or drag intervals to compute the selected average
        </div>
      </div>

      <div className="grid gap-2 p-2 lg:grid-cols-[minmax(0,1fr)_17rem]">
        <div
          ref={chartBoxRef}
          className="relative min-w-0"
          onMouseLeave={() => setHover(null)}
        >
          <svg
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            role="img"
            aria-label="OPCOM DAM price chart"
            className="h-[21rem] w-full select-none"
          >
            <rect
              x={CHART_LEFT}
              y={PLOT_TOP}
              width={plotWidth}
              height={PLOT_HEIGHT}
              fill="rgb(var(--border))"
              opacity="0.035"
              rx="4"
            />

            {yTicks.map((tick) => {
              const y = yForPrice(tick);
              return (
                <g key={`price-tick-${tick}`}>
                  <line
                    x1={CHART_LEFT}
                    x2={CHART_RIGHT}
                    y1={y}
                    y2={y}
                    stroke="rgb(var(--border))"
                    strokeOpacity="0.55"
                    strokeWidth="1"
                  />
                  <text
                    x={CHART_LEFT - 8}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill="rgb(var(--muted))"
                    fontSize="11"
                  >
                    {formatAxisNumber(tick)}
                  </text>
                </g>
              );
            })}

            {xTicks.map(({ idx, label }) => {
              const x = xForIndex(idx);
              return (
                <g key={`x-tick-${idx}`}>
                  <line
                    x1={x}
                    x2={x}
                    y1={PLOT_TOP}
                    y2={PLOT_BOTTOM}
                    stroke="rgb(var(--border))"
                    strokeOpacity="0.22"
                    strokeWidth="1"
                  />
                  <text
                    x={x}
                    y={CHART_HEIGHT - 20}
                    textAnchor="middle"
                    fill="rgb(var(--muted))"
                    fontSize="11"
                  >
                    {label}
                  </text>
                </g>
              );
            })}

            <text x="12" y={PLOT_TOP + 12} fill="rgb(var(--muted))" fontSize="11">
              {unitLabel(currency)}
            </text>

            {rows.map((row, idx) => {
              if (!selectedIds.has(row.id)) return null;
              const x = CHART_LEFT + idx * step;
              return (
                <g key={`selected-interval-${row.id}`} pointerEvents="none">
                  <rect
                    x={x}
                    y={PLOT_TOP}
                    width={step}
                    height={PLOT_HEIGHT}
                    fill="rgb(var(--accent))"
                    opacity="0.08"
                  />
                  <rect
                    x={x + 0.6}
                    y={PLOT_TOP + 0.6}
                    width={Math.max(0, step - 1.2)}
                    height={PLOT_HEIGHT - 1.2}
                    fill="none"
                    stroke="rgb(var(--accent))"
                    strokeOpacity="0.55"
                    strokeWidth="1.2"
                  />
                </g>
              );
            })}

            {hover && hoveredRow && (
              <rect
                x={CHART_LEFT + hover.rowIndex * step}
                y={PLOT_TOP}
                width={step}
                height={PLOT_HEIGHT}
                fill="rgb(var(--pos-green))"
                opacity="0.14"
                pointerEvents="none"
              />
            )}

            {lineSegments(
              rows.map((row) => priceFor(row, currency)),
              xForIndex,
              yForPrice
            ).map((path, idx) => (
              <path
                key={`dam-price-line-${idx}`}
                d={path}
                fill="none"
                stroke="rgb(var(--accent))"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {showPoints &&
              rows.map((row, idx) => {
                const value = priceFor(row, currency);
                if (!isFiniteNumber(value)) return null;
                return (
                  <circle
                    key={`dam-price-point-${row.id}`}
                    cx={xForIndex(idx)}
                    cy={yForPrice(value)}
                    r="1.8"
                    fill="rgb(var(--accent))"
                    opacity="0.75"
                  />
                );
              })}

            {rows.map((row, idx) => (
              <rect
                key={`hover-band-${row.id}`}
                x={CHART_LEFT + idx * step}
                y={PLOT_TOP}
                width={step}
                height={PLOT_HEIGHT}
                fill="transparent"
                pointerEvents="all"
                onMouseDown={(e) => beginSelection(idx, e)}
                onMouseMove={(e) => extendSelection(idx, e)}
                onMouseEnter={(e) => extendSelection(idx, e)}
              />
            ))}
          </svg>

          {hover && hoveredRow && (
            <DamChartTooltip
              row={hoveredRow}
              currency={currency}
              style={{ left: hover.left, top: hover.top }}
            />
          )}
        </div>
        <DamSelectionPanel stats={selectionStats} currency={currency} onClear={clearSelection} />
      </div>
    </div>
  );
}

function DamChartTooltip({
  row,
  currency,
  style,
}: {
  row: OpcomDamInterval;
  currency: PriceCurrency;
  style: React.CSSProperties;
}) {
  return (
    <div
      className="pointer-events-none absolute z-30 w-[282px] max-w-[calc(100%-16px)] rounded-md border border-border/80 bg-bg/95 px-3 py-2 text-[11px] shadow-lg backdrop-blur"
      style={style}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-border/50 pb-1 text-muted">
        <span>{row.deliveryDate} QH-{row.qhIndex}</span>
        <span>{cetIntervalLabel(row.intervalStart, row.intervalEnd)}</span>
      </div>
      <TooltipLine label={unitLabel(currency)} value={priceFor(row, currency)} strong />
      <TooltipLine
        label={currency === "EUR" ? "RON/MWh" : "EUR/MWh"}
        value={currency === "EUR" ? row.priceRonMwh : row.priceEurMwh}
      />
      <div className="mt-1.5 border-t border-border/40 pt-1.5 text-muted">
        <div>Source: OPCOM {row.sourceGranularity}</div>
        <div>
          Source interval {row.sourceIntervalIndex}/{row.sourceIntervalCount}
        </div>
        <div>FX: {row.fx.rate > 0 ? row.fx.rate.toFixed(4) : "-"} {row.fx.source}</div>
      </div>
    </div>
  );
}

function TooltipLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number | null;
  strong?: boolean;
}) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-3", strong && "font-semibold")}>
      <span className="text-muted">{label}</span>
      <span className="text-right font-mono text-fg">{formatPrice(value)}</span>
    </div>
  );
}

function DamSelectionPanel({
  stats,
  currency,
  onClear,
}: {
  stats: SelectionStats | null;
  currency: PriceCurrency;
  onClear: () => void;
}) {
  return (
    <aside className="min-w-0 border-t border-border/50 pt-2 text-[11px] lg:border-l lg:border-t-0 lg:pl-3 lg:pt-0">
      <div className="mb-2 flex items-start justify-between gap-2 border-b border-border/40 pb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted">Selection</div>
          <div className="truncate font-mono text-xs text-fg" title={stats?.rangeLabel}>
            {stats ? `${stats.count} QHs - ${stats.rangeLabel}` : "0 QHs"}
          </div>
        </div>
        {stats && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border/70 text-muted hover:border-accent/70 hover:text-fg"
            title="clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {stats ? (
        <div className="space-y-1.5 font-mono">
          <SelectionLine label={`Avg ${unitLabel(currency)}`} value={stats.avg} strong />
          <SelectionLine label="Min" value={stats.min} />
          <SelectionLine label="Max" value={stats.max} />
        </div>
      ) : (
        <div className="text-muted">Click or drag over the chart to calculate an average.</div>
      )}
    </aside>
  );
}

function SelectionLine({ label, value, strong = false }: { label: string; value: number | null; strong?: boolean }) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-2", strong && "font-semibold")}>
      <span className="font-sans text-muted">{label}</span>
      <span className="text-right text-fg">{formatPrice(value)}</span>
    </div>
  );
}

function CoveragePanel({ days, currency }: { days: OpcomDamDay[]; currency: PriceCurrency }) {
  return (
    <div className="border border-border/60 rounded-md overflow-hidden">
      <div className="border-b border-border/50 px-3 py-2 text-xs font-medium">Daily coverage</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted">
            <tr className="border-b border-border/50">
              <Th>Date</Th>
              <Th>Status</Th>
              <Th>Rows</Th>
              <Th>Granularity</Th>
              <Th align="right">ROPEX Base</Th>
              <Th align="right">ROPEX Peak</Th>
              <Th align="right">Avg</Th>
              <Th>Fetched</Th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.deliveryDate} className="border-b border-border/30 last:border-0">
                <Td className="font-mono">{day.deliveryDate}</Td>
                <Td>
                  <span className="inline-flex items-center gap-2">
                    <StatusDot status={day.status} />
                    {day.status}
                  </span>
                </Td>
                <Td className="font-mono">
                  {day.rowCount}/{day.expectedRows}
                </Td>
                <Td>{sourceGranularityLabel(day.sourceGranularity)}</Td>
                <Td align="right">{formatPrice(convertDayPrice(day.ropexBaseEurMwh, day, currency))}</Td>
                <Td align="right">{formatPrice(convertDayPrice(day.ropexPeakEurMwh, day, currency))}</Td>
                <Td align="right">{formatPrice(convertDayPrice(day.avgPriceEurMwh, day, currency))}</Td>
                <Td>
                  <span title={day.latestFetch?.warnings.join("\n") || day.latestFetch?.error || undefined}>
                    {formatTimestamp(day.fetchedAt ?? day.latestFetch?.finishedAt)}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th className={cn("px-3 py-2 font-medium", align === "right" && "text-right")}>
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  className,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td className={cn("px-3 py-2 text-muted", align === "right" && "text-right", className)}>
      {children}
    </td>
  );
}

function StatusDot({ status }: { status: DayStatus }) {
  const className =
    status === "complete"
      ? "bg-posGreen"
      : status === "partial"
      ? "bg-warnOrange"
      : status === "empty" || status === "missing"
      ? "bg-muted"
      : "bg-negRed";
  return <span className={cn("h-2 w-2 rounded-full", className)} />;
}

function buildSelectionStats(rows: OpcomDamInterval[], currency: PriceCurrency): SelectionStats | null {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort(
    (a, b) => new Date(a.intervalStart).getTime() - new Date(b.intervalStart).getTime()
  );
  return {
    ...priceStats(sorted, currency),
    rangeLabel: selectedRangeLabel(sorted),
  };
}

function priceStats(rows: OpcomDamInterval[], currency: PriceCurrency): PriceStats {
  const values = rows.map((row) => priceFor(row, currency)).filter(isFiniteNumber);
  return {
    count: rows.length,
    avg: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null,
    min: values.length ? Math.min(...values) : null,
    max: values.length ? Math.max(...values) : null,
  };
}

function selectedRangeLabel(rows: OpcomDamInterval[]): string {
  if (rows.length === 0) return "";
  const ranges: Array<{ start: OpcomDamInterval; end: OpcomDamInterval }> = [];
  for (const row of rows) {
    const last = ranges[ranges.length - 1];
    if (last && row.deliveryDate === last.end.deliveryDate && row.qhIndex === last.end.qhIndex + 1) {
      last.end = row;
    } else {
      ranges.push({ start: row, end: row });
    }
  }
  return ranges
    .map((range) => {
      const start = cetTimeLabel(range.start.intervalStart);
      const end = cetTimeLabel(range.end.intervalEnd);
      const qh =
        range.start.id === range.end.id
          ? `QH-${range.start.qhIndex}`
          : `QH-${range.start.qhIndex}-${range.end.qhIndex}`;
      const date =
        range.start.deliveryDate === range.end.deliveryDate
          ? range.start.deliveryDate
          : `${range.start.deliveryDate}..${range.end.deliveryDate}`;
      return `${date} ${qh} ${start}-${end}`;
    })
    .join(", ");
}

function priceFor(row: OpcomDamInterval, currency: PriceCurrency): number | null {
  return currency === "EUR" ? row.priceEurMwh : row.priceRonMwh;
}

function convertDayPrice(valueEur: number | null, day: OpcomDamDay, currency: PriceCurrency): number | null {
  if (valueEur === null) return null;
  if (currency === "EUR") return valueEur;
  return day.fx.rate > 0 ? valueEur * day.fx.rate : null;
}

function unitLabel(currency: PriceCurrency): string {
  return `${currency}/MWh`;
}

function coverageLabel(days: OpcomDamDay[]): string {
  const complete = days.filter((day) => day.status === "complete").length;
  return `${complete}/${days.length} days`;
}

function granularityLabel(days: OpcomDamDay[]): string {
  const labels = [...new Set(days.map((day) => sourceGranularityLabel(day.sourceGranularity)))].filter(
    (label) => label !== "-"
  );
  return labels.length ? labels.join(", ") : "-";
}

function sourceGranularityLabel(value: SourceGranularity): string {
  if (value === "HOUR") return "hourly expanded";
  if (value === "QH") return "15-min native";
  if (value === "MIXED") return "mixed";
  return "-";
}

function rangeLabel(fromDate: string, toDate: string): string {
  return fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`;
}

function paddedRange(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (Math.abs(max - min) < CHART_EPS) {
    const pad = Math.max(Math.abs(max) * 0.08, 1);
    return { min: min - pad, max: max + pad };
  }
  const pad = (max - min) * 0.08;
  return { min: min - pad, max: max + pad };
}

function makeTicks(min: number, max: number, count: number): number[] {
  if (count <= 1) return [min];
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, idx) => min + idx * step);
}

function makeXTicks(rows: OpcomDamInterval[], isRange: boolean): Array<{ idx: number; label: string }> {
  if (rows.length === 0) return [];
  const target = Math.min(isRange ? 8 : 7, rows.length);
  const indexes = new Set<number>();
  for (let i = 0; i < target; i++) {
    indexes.add(Math.round((i * (rows.length - 1)) / Math.max(1, target - 1)));
  }
  return [...indexes].sort((a, b) => a - b).flatMap((idx) => {
    const row = rows[idx];
    if (!row) return [];
    return {
      idx,
      label: isRange ? compactCetDateTimeLabel(row.intervalStart) : cetTimeLabel(row.intervalStart),
    };
  });
}

function lineSegments(
  values: Array<number | null>,
  xForIndex: (idx: number) => number,
  yForValue: (value: number) => number
): string[] {
  const segments: string[] = [];
  let path = "";
  values.forEach((value, idx) => {
    if (!isFiniteNumber(value)) {
      if (path) segments.push(path);
      path = "";
      return;
    }
    const x = xForIndex(idx);
    const y = yForValue(value);
    path += path ? ` L ${x.toFixed(2)} ${y.toFixed(2)}` : `M ${x.toFixed(2)} ${y.toFixed(2)}`;
  });
  if (path) segments.push(path);
  return segments;
}

function formatAxisNumber(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function cetIntervalLabel(startIso: string, endIso: string): string {
  return `${cetTimeLabel(startIso)}-${cetTimeLabel(endIso)} CET`;
}

function cetTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "??:??";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: CET_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function compactCetDateTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: CET_TIME_ZONE,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("day")}.${part("month")} ${part("hour")}:${part("minute")}`;
}

function todayCetIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: CET_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}
